import { Card, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { formatInTimeZone } from "date-fns-tz";

import { type SupportShiftDetails } from "../../api/useShiftDetails";
import { EmptyStateText } from "../../components/EmptyStateText";
import { formatDurationFromHours } from "../../utils/formatters";
import { ShiftInfoRow } from "./ShiftInfoRow";

interface TimekeepingCardProps {
  readonly shiftDetails: SupportShiftDetails;
  readonly workplaceTimezone: string;
  readonly isVerified: boolean;
}

export function TimekeepingCard({
  shiftDetails,
  workplaceTimezone,
  isVerified,
}: TimekeepingCardProps) {
  const hasClockData = isDefined(shiftDetails.clockInTime) || isDefined(shiftDetails.clockOutTime);

  return (
    <Card variant="tertiary" sx={(theme) => ({ padding: theme.spacing(5) })}>
      <Stack spacing={3}>
        <Text semibold variant="body1">
          Timekeeping
        </Text>
        {hasClockData ? (
          <TimekeepingDetails
            shiftDetails={shiftDetails}
            workplaceTimezone={workplaceTimezone}
            isVerified={isVerified}
          />
        ) : (
          <EmptyStateText>Clock in and out times weren&apos;t recorded</EmptyStateText>
        )}
      </Stack>
    </Card>
  );
}

interface TimekeepingDetailsProps {
  readonly shiftDetails: SupportShiftDetails;
  readonly workplaceTimezone: string;
  readonly isVerified: boolean;
}

function TimekeepingDetails({
  shiftDetails,
  workplaceTimezone,
  isVerified,
}: TimekeepingDetailsProps) {
  return (
    <>
      {isDefined(shiftDetails.clockInTime) && (
        <ShiftInfoRow
          label="Clock In"
          value={formatTime(shiftDetails.clockInTime, workplaceTimezone)}
        />
      )}
      {isDefined(shiftDetails.clockOutTime) && (
        <ShiftInfoRow
          label="Clock Out"
          value={formatTime(shiftDetails.clockOutTime, workplaceTimezone)}
        />
      )}
      {isDefined(shiftDetails.lunchStartTime) && isDefined(shiftDetails.lunchEndTime) && (
        <ShiftInfoRow
          label="Break"
          value={`${formatTime(shiftDetails.lunchStartTime, workplaceTimezone)} - ${formatTime(
            shiftDetails.lunchEndTime,
            workplaceTimezone
          )}`}
        />
      )}
      {isVerified && isDefined(shiftDetails.actualDurationHours) && (
        <ShiftInfoRow
          label="Time Worked"
          value={formatDurationFromHours(shiftDetails.actualDurationHours)}
        />
      )}
    </>
  );
}

function formatTime(isoString: string, timezone: string): string {
  return formatInTimeZone(parseISO(isoString), timezone, "h:mm a");
}
